export const THICKNESSES = [6, 15, 18, 25];
export const SIDES = ["C1", "C2", "L1", "L2"];
export const SIDE_LABELS = {
  C1: "Comprimento 1",
  C2: "Comprimento 2",
  L1: "Largura 1",
  L2: "Largura 2",
};
export const THICK_BADGE = {
  6: "bg-amber-950/60 text-amber-300 border-amber-800",
  15: "bg-blue-950/60 text-blue-300 border-blue-800",
  18: "bg-emerald-950/60 text-emerald-300 border-emerald-800",
  25: "bg-purple-950/60 text-purple-300 border-purple-800",
};
export const SHEET = { length: 2750, width: 1850, trim: 10, kerf: 6.5 };

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));

export function newPiece(defaults = {}, over = {}) {
  return {
    id: uid(),
    name: "",
    length: 500,
    width: 300,
    qty: 1,
    thickness: defaults.thickness || 18,
    mdf_color: defaults.mdf_color || "",
    edge_color: defaults.edge_color || "",
    edges: { C1: false, C2: false, L1: false, L2: false },
    hinge: { qty: 0, side: "C1" },
    groove: { enabled: false, side: "C1", offset: 12, depth: 8, width: 6.6 },
    rabbet: { enabled: false, side: "C2", width: 7, depth: 10 },
    led: { enabled: false, side: "C1", offset: 20, depth: 8, width: 10 },
    backch: { enabled: false, side: "C2", offset: 12, depth: 8, width: 7 },
    radius: 0,
    corners: { C1L1: false, C1L2: false, C2L1: false, C2L2: false },
    tampon: { enabled: false, type: "dupla", width: 50, thickness: 18, sides: { C1: true, C2: false, L1: false, L2: false } },
    grain: false,
    module: "",
    ...over,
    id: uid(),
  };
}

export const pieceLabel = (n) => `P${String(n).padStart(3, "0")}`;

export function edgesText(p) {
  const s = SIDES.filter((k) => p.edges?.[k]);
  if (p.notch?.enabled && p.notch.edge) s.push("frente do L");
  return s.length ? s.join(", ") : "Sem fita";
}

export function machiningText(p) {
  const out = [];
  if (p.hinge?.qty > 0) out.push(`Dobradiça ${p.hinge.qty}x ${p.hinge.side}`);
  if (p.groove?.enabled) out.push(`Canal fundo ${p.groove.width || 6.6}mm ${p.groove.side} a ${p.groove.offset}mm`);
  if (p.rabbet?.enabled) out.push(`Rebaixo fundo ${p.rabbet.side} ${p.rabbet.width}x${p.rabbet.depth}mm`);
  if (p.led?.enabled) out.push(`Canal LED ${p.led.side} ${p.led.width}mm`);
  if (p.backch?.enabled) out.push(`Canal p/ fundo ${p.backch.side} ${p.backch.width}mm a ${p.backch.offset}mm`);
  const rc = CORNER_KEYS.filter((k) => p.corners?.[k]);
  if (+p.radius > 0 && rc.length) out.push(`Raio ${p.radius}mm (${rc.length} cantos)`);
  if (p.tampon?.enabled) out.push(p.tampon.type === "regua" ? `Tamp. régua ${p.tampon.width}mm` : `Tamp. peça dupla`);
  if (p.derived) out.push("Peça de tamponamento");
  if (p.notch?.enabled) out.push(`Peça em L (recorte ${p.notch.length}x${p.notch.width})`);
  if (p.slot?.enabled) out.push(`Encaixe da régua ${p.slot.length}x${p.slot.width}`);
  if (p.frame?.enabled) out.push(`Moldura provençal ${p.frame.width}mm (vazada)`);
  if (p.drills?.length) out.push(`${p.drills.length} furos Ø8mm prof. ${p.drills[0].depth}mm`);
  return out.join(" · ");
}

export const CORNER_KEYS = ["C1L1", "C1L2", "C2L2", "C2L1"];
export const CORNER_LABELS = { C1L1: "C1 + L1", C1L2: "C1 + L2", C2L1: "C2 + L1", C2L2: "C2 + L2" };

export function cornerRadii(p) {
  const c = p.corners || {}, L = +p.length, W = +p.width;
  const limL = (c.C1L1 && c.C1L2) || (c.C2L1 && c.C2L2) ? L / 2 : L;
  const limW = (c.C1L1 && c.C2L1) || (c.C1L2 && c.C2L2) ? W / 2 : W;
  const r = Math.max(0, Math.min(+p.radius || 0, limL, limW));
  return CORNER_KEYS.map((k) => (c[k] ? r : 0));
}

// SVG path of the piece outline; map(x, y) converts local mm (y up) to SVG coords
export function outlinePath(p, map) {
  const L = +p.length, W = +p.width;
  const pt = (x, y) => map(x, y).join(" ");
  if (p.notch?.enabled) {
    const nl = +p.notch.length, nw = +p.notch.width;
    const sl = p.slot?.enabled ? +p.slot.length : 0, sw = p.slot?.enabled ? +p.slot.width : 0;
    const start = sl ? `M${pt(sl, 0)}` : `M${pt(0, 0)}`;
    const tail = sl ? ` L${pt(0, sw)} L${pt(sl, sw)}` : "";
    return `${start} L${pt(L, 0)} L${pt(L, W - nw)} L${pt(L - nl, W - nw)} L${pt(L - nl, W)} L${pt(0, W)}${tail} Z`;
  }
  const [r1, r2, r3, r4] = cornerRadii(p);
  const fw = p.frame?.enabled ? +p.frame.width : 0;
  const hole = fw ? ` M${pt(fw, fw)} L${pt(fw, W - fw)} L${pt(L - fw, W - fw)} L${pt(L - fw, fw)} Z` : "";
  const pts = [[r1, 0, 0], [L - r2, 0, 0], [L, r2, r2], [L, W - r3, 0], [L - r3, W, r3], [r4, W, 0], [0, W - r4, r4], [0, r1, 0], [r1, 0, r1]];
  return pts.map(([x, y, r], i) => {
    const [sx, sy] = map(x, y);
    if (i === 0) return `M${sx} ${sy}`;
    return r ? `A${r} ${r} 0 0 0 ${sx} ${sy}` : `L${sx} ${sy}`;
  }).join(" ") + " Z" + hole;
}

export function hingePositions(span, qty) {
  if (qty <= 0) return [];
  if (qty === 1) return [span / 2];
  const m = span >= 300 ? 100 : span * 0.2;
  const step = (span - 2 * m) / (qty - 1);
  return Array.from({ length: qty }, (_, i) => m + i * step);
}

export function pieceFeatures(p) {
  const L = +p.length, W = +p.width;
  const holes = [];
  const side = p.hinge?.side || "C1";
  const span = side.startsWith("C") ? L : W;
  hingePositions(span, +p.hinge?.qty || 0).forEach((pos) => {
    holes.push({ C1: [pos, 22.5], C2: [pos, W - 22.5], L1: [22.5, pos], L2: [L - 22.5, pos] }[side]);
  });
  const grooves = [];
  if (p.groove?.enabled) grooves.push(channelRect(p.groove.side, +p.groove.offset || 0, +p.groove.width || 6.6, L, W));
  if (p.rabbet?.enabled) grooves.push(channelRect(p.rabbet.side || "C2", -(+p.rabbet.over || 0), (+p.rabbet.width || 7) + (+p.rabbet.over || 0), L, W));
  const leds = [];
  if (p.led?.enabled) leds.push(channelRect(p.led.side, +p.led.offset || 0, +p.led.width || 10, L, W));
  const drills = (p.drills || []).map((d) => [+d.x, +d.y]);
  return { holes, grooves, leds, drills };
}

function channelRect(side, o, w, L, W, e = 5) {
  return { C1: [-e, o, L + 2 * e, w], C2: [-e, W - o - w, L + 2 * e, w], L1: [o, -e, w, W + 2 * e], L2: [L - o - w, -e, w, W + 2 * e] }[side];
}

// Local edge-band segments [x1, y1, x2, y2] (mm, y up), including the inner edges of L pieces
export function localEdgeSegs(p) {
  const L = +p.length, W = +p.width;
  const nt = p.notch?.enabled ? [+p.notch.length, +p.notch.width] : [0, 0];
  const [nl, nw] = nt;
  const m = { C1: [0, 0, L, 0], C2: [0, W, L - nl, W], L1: [0, 0, 0, W], L2: [L, 0, L, W - nw] };
  const out = SIDES.filter((k) => p.edges?.[k]).map((k) => m[k]);
  if (nl && p.notch.edge) out.push([L, W - nw, L - nl, W - nw], [L - nl, W - nw, L - nl, W]);
  return out;
}

// Mirrors backend expand_tampon: extra pieces generated by tamponamento
export function tamponPieces(p) {
  const t = p.tampon;
  if (!t?.enabled) return [];
  const base = { ...p, hinge: { qty: 0, side: "C1" }, groove: { enabled: false }, led: { enabled: false }, rabbet: { enabled: false }, tampon: { enabled: false },
    thickness: +t.thickness || p.thickness, derived: true };
  const none = { C1: false, C2: false, L1: false, L2: false };
  if (t.type === "regua") {
    const ends = { C1: ["C1L1", "C1L2"], C2: ["C2L1", "C2L2"], L1: ["C1L1", "C2L1"], L2: ["C1L2", "C2L2"] };
    return SIDES.filter((s) => t.sides?.[s]).map((s) => {
      const w0 = Math.min(+t.width || 50, 1830);
      // side rails between the length rails are shorter and always straight
      const inner = s.startsWith("C") ? 0 : ["C1", "C2"].filter((c) => t.sides?.[c]).length;
      const length = (s.startsWith("C") ? +p.length : +p.width) - inner * w0;
      const rad = p.notch?.enabled || inner ? 0 : Math.min(+p.radius || 0, length / 2);
      // rail follows the same radius as the top; widened when the radius is larger than the rail
      const w = Math.min(Math.max(w0, rad), 1830);
      const corners = rad ? { C1L1: !!p.corners?.[ends[s][0]], C1L2: !!p.corners?.[ends[s][1]] } : {};
      return {
        ...base, id: `${p.id}-r${s}`, name: `Régua ${s} - ${p.name}`, length, width: w,
        radius: Object.values(corners).some(Boolean) ? rad : 0, corners, notch: {}, edges: { ...none, C1: !!p.edges?.[s] },
      };
    });
  }
  return [{ ...base, id: `${p.id}-d`, name: `Dupla - ${p.name}`, edges: none }];
}

export function edgeMeters(pieces) {
  const t = {};
  pieces.forEach((p) => {
    const mm = localEdgeSegs(p).reduce((s, [a, b, c, d]) => s + Math.abs(c - a) + Math.abs(d - b), 0) * p.qty;
    if (mm) {
      const c = p.edge_color?.trim() || "Sem cor";
      t[c] = (t[c] || 0) + mm / 1000;
    }
  });
  return t;
}
